import { Instrument } from '../types';

/**
 * Checks whether an instrument is a user-custom, user-edited, or user-uploaded product.
 * Returns true if the user uploaded an image from their computer, edited details, or created the product.
 */
export const isUserCustomProduct = (item: any): boolean => {
  if (!item) return false;

  // 1. Explicit user protection flags
  if (item.isUserCustom || item.userProtected || item.isUserEdited) {
    return true;
  }

  // 2. Custom local/server image upload or data URL
  const img = item.imageUrl || '';
  if (img.startsWith('/uploads/') || img.startsWith('data:') || img.startsWith('blob:')) {
    return true;
  }

  // 3. Any external or custom image URL that is not the default unsplash stock image
  if (img && !img.includes('images.unsplash.com')) {
    return true;
  }

  // 4. Secondary or third custom angle photos
  if (item.image2Url && (item.image2Url.startsWith('/uploads/') || item.image2Url.startsWith('data:'))) {
    return true;
  }
  if (item.image3Url && (item.image3Url.startsWith('/uploads/') || item.image3Url.startsWith('data:'))) {
    return true;
  }

  // 5. Custom ID that does not match the default seed numbering
  if (item.id && !item.id.startsWith('inst-') && !item.id.startsWith('seed-')) {
    return true;
  }

  return false;
};

/**
 * Universal Sorting Function:
 * Places ALL user-edited products and custom-image products at the VERY TOP (starting position).
 * Stock/AI-provided items are placed after/below user items.
 * Within each tier, newest edited/created items appear first.
 */
export const sortInstrumentsWithUserPriority = <T extends Partial<Instrument>>(items: T[]): T[] => {
  if (!Array.isArray(items)) return [];

  return [...items].sort((a, b) => {
    const isUserA = isUserCustomProduct(a);
    const isUserB = isUserCustomProduct(b);

    // Tier 1: User-edited & custom-uploaded products rank FIRST
    if (isUserA && !isUserB) return -1;
    if (!isUserA && isUserB) return 1;

    // Tier 2: Within the same tier, sort by newest update or creation time
    const timeA = a.updatedAt ? new Date(a.updatedAt).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
    const timeB = b.updatedAt ? new Date(b.updatedAt).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
    if (timeA && timeB && timeA !== timeB) {
      return timeB - timeA;
    }

    // Fallback: Alphabetical order by name
    return (a.name || '').localeCompare(b.name || '');
  });
};
